import { PharmaCompanyMultiSelect } from '@/entities/filter/ui/PharmaCompanyMultiSelect';
import { usePharmaCompanyOptions } from '@/entities/filter/hooks/useFilterOptions';
import type {
  ImageArchiveJob,
  PrescriptionExportRequest,
} from '@/entities/prescription-registration/api/prescription-registration.api';
import {
  formatRegistrationMonth,
  type RegistrationPeriodOption,
} from '@/entities/prescription-registration/registrationMonth';
import { formatCount } from '@/shared/lib/format';
import {
  Button,
  Modal,
  ModalField,
  SegmentedControl,
  Select,
} from '@/shared/ui';
import { toastApi } from '@/shared/utils/toast';
import { useEffect, useState } from 'react';
import type { PrescriptionDownloads } from './usePrescriptionDownloads';

/** 다운로드 대상 — 전체 = 엑셀 + 이미지 ZIP */
type DownloadTarget = 'ALL' | 'IMAGES' | 'EXCEL';

const TARGET_OPTIONS: { value: DownloadTarget; label: string }[] = [
  { value: 'ALL', label: '전체' },
  { value: 'IMAGES', label: '처방 이미지' },
  { value: 'EXCEL', label: '파일' },
];

type PharmaMode = 'ALL' | 'SELECTED';

const PHARMA_MODE_OPTIONS: { value: PharmaMode; label: string }[] = [
  { value: 'ALL', label: '전체 제약사' },
  { value: 'SELECTED', label: '제약사 선택' },
];

const includesImages = (target: DownloadTarget) => target !== 'EXCEL';
const includesExcel = (target: DownloadTarget) => target !== 'IMAGES';

const archiveProgress = (job: ImageArchiveJob | undefined) =>
  job
    ? `처방 이미지 ${formatCount(job.targetCount, '장')}을 압축하고 있어요. 끝나면 자동으로 내려받아요.`
    : '처방 이미지 압축을 요청하고 있어요.';

interface PrescriptionDownloadDialogProps {
  open: boolean;
  onClose: () => void;
  /** 화면에서 고른 등록·정산 월 */
  period: RegistrationPeriodOption;
  /** 같은 등록 월에서 고를 수 있는 정산 월(yyyyMM) — /months 의 조합 */
  settlementMonths: string[];
  /** 목록 검색어(병의원명·요양기관번호) — 그대로 넘긴다 */
  keyword: string | undefined;
  /** 화면 요약의 등록 이미지 수 — 조건을 좁히지 않았을 때만 대상 수로 보인다 */
  imageCount: number | undefined;
  downloads: PrescriptionDownloads;
}

/**
 * Figma 처방 내역 다운로드 모달 — 대상(전체 · 처방 이미지 · 파일) · 정산 년월 · 제약사.
 * 등록 월과 검색어는 화면 조건을 따른다. 담당자·등록 상태 필터는 API 가 받지 않는다.
 */
export function PrescriptionDownloadDialog({
  open,
  onClose,
  period,
  settlementMonths,
  keyword,
  imageCount,
  downloads: { archive, excel },
}: PrescriptionDownloadDialogProps) {
  const [target, setTarget] = useState<DownloadTarget>('ALL');
  const [settlementMonth, setSettlementMonth] = useState<string>();
  const [pharmaMode, setPharmaMode] = useState<PharmaMode>('ALL');
  const [pharmaCompanyIds, setPharmaCompanyIds] = useState<number[]>([]);
  const [isSubmitting, setSubmitting] = useState(false);
  const { data: pharmaCompanies = [] } = usePharmaCompanyOptions();

  // 열 때마다 화면 조건으로 시작한다
  useEffect(() => {
    if (!open) return;
    setTarget('ALL');
    setSettlementMonth(period.settlementYearMonth ?? undefined);
    setPharmaMode('ALL');
    setPharmaCompanyIds([]);
    // period 객체는 렌더마다 같지 않을 수 있다 — 열릴 때만 맞춘다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const registrationMonth = formatRegistrationMonth(
    period.registrationYearMonth,
  );
  const isPharmaSelected = pharmaMode === 'SELECTED';
  const selectedPharmaNames = pharmaCompanyIds.map(
    (id) =>
      pharmaCompanies.find((company) => company.pharmaCompanyId === id)
        ?.pharmaCompanyName ?? `#${id}`,
  );
  const request: PrescriptionExportRequest = {
    registrationYearMonth: period.registrationYearMonth,
    settlementYearMonth: settlementMonth,
    keyword,
    pharmaCompanyIds: isPharmaSelected ? pharmaCompanyIds : undefined,
  };
  // 화면 요약과 같은 조건일 때만 장수를 미리 안다
  const isScreenScope =
    !keyword &&
    !isPharmaSelected &&
    settlementMonth === (period.settlementYearMonth ?? undefined);

  const isArchiveBlocked = includesImages(target) && archive.isRunning;
  const canSubmit =
    !isSubmitting &&
    !isArchiveBlocked &&
    !(isPharmaSelected && pharmaCompanyIds.length === 0);

  const submit = async () => {
    setSubmitting(true);
    const [excelResult, archiveResult] = await Promise.allSettled([
      includesExcel(target) ? excel.mutateAsync(request) : undefined,
      includesImages(target) ? archive.start(request) : undefined,
    ]);
    setSubmitting(false);
    // 오류는 각 훅이 안내한다. 하나만 실패하면 실패한 쪽만 다시 받을 수 있게 남긴다
    if (
      excelResult.status === 'rejected' ||
      archiveResult.status === 'rejected'
    ) {
      if (excelResult.status === 'fulfilled' && includesExcel(target))
        setTarget('IMAGES');
      if (archiveResult.status === 'fulfilled' && includesImages(target))
        setTarget('EXCEL');
      return;
    }
    if (includesImages(target))
      toastApi.info(
        '처방 이미지를 압축하고 있어요. 끝나면 자동으로 내려받아요.',
      );
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="처방 내역 다운로드"
      footer={
        <>
          <Button variant="outline" size="md" onClick={onClose}>
            취소
          </Button>
          <Button
            variant="accent"
            size="md"
            disabled={!canSubmit}
            isLoading={isSubmitting}
            onClick={submit}
          >
            다운로드
          </Button>
        </>
      }
    >
      <ModalField
        label="다운로드 대상"
        hint="전체 = 엑셀 + 처방전 이미지(.zip) · 처방 이미지 = 이미지만(.zip) · 파일 = 엑셀만(.xlsx)"
      >
        <SegmentedControl
          label="다운로드 대상"
          value={target}
          options={TARGET_OPTIONS}
          onChange={setTarget}
        />
      </ModalField>

      <ModalField
        label="정산 년월"
        hint={
          settlementMonth
            ? `처방 등록 월 ${registrationMonth}의 처방 내역이 ${formatRegistrationMonth(settlementMonth)}에 정산돼요.`
            : `처방 등록 월 ${registrationMonth}의 처방 내역을 정산 월 구분 없이 받아요.`
        }
      >
        <Select
          label="정산 년월"
          showLabel={false}
          allLabel="정산 월 전체"
          icon="calendar"
          value={settlementMonth}
          options={settlementMonths.map((month) => ({
            value: month,
            label: formatRegistrationMonth(month),
          }))}
          onChange={setSettlementMonth}
          className="w-full rounded-12"
        />
      </ModalField>

      <ModalField
        label="제약사"
        hint={
          isPharmaSelected
            ? '여러 개 선택할 수 있어요. 엑셀은 고른 제약사의 처방만, 이미지는 그 제약사와 거래한 병의원 것만 담겨요.'
            : undefined
        }
      >
        <SegmentedControl
          label="제약사"
          size="sm"
          value={pharmaMode}
          options={PHARMA_MODE_OPTIONS}
          onChange={setPharmaMode}
        />
        {isPharmaSelected && (
          <PharmaCompanyMultiSelect
            value={pharmaCompanyIds}
            onChange={setPharmaCompanyIds}
          />
        )}
      </ModalField>

      <div className="flex w-full flex-col gap-1 rounded-8 bg-surface-grey px-3.5 py-3">
        <p className="text-body-13 font-regular text-fg-tertiary">
          현재 화면 조건이 함께 적용돼요: 처방 등록 월 {registrationMonth} ·
          병의원 검색 {keyword ? `'${keyword}'` : '전체'} · 제약사{' '}
          {isPharmaSelected && selectedPharmaNames.length > 0
            ? selectedPharmaNames.join(', ')
            : '전체'}
          . 담당자·등록 상태 필터는 적용되지 않아요.
        </p>
        <p className="text-body-13 font-medium text-fg-primary">
          {archive.isRunning
            ? archiveProgress(archive.job)
            : includesImages(target) && isScreenScope && imageCount != null
              ? `대상: 처방전 이미지 ${formatCount(imageCount, '장')}`
              : '대상 건수는 내려받을 때 정해져요.'}
        </p>
      </div>
    </Modal>
  );
}
